import {
  Box,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
} from '@spaceship-fspl/components';
import {
  backgroundColor,
  height,
  left,
  marginTop,
  match,
  padding,
  paddingRight,
  position,
  right,
  top,
  width,
  zIndex,
} from '@spaceship-fspl/styles';
import { DottedCircle } from 'components';
import * as React from 'react';
import styled from 'styled-components';

import squiggleIcon from './img-ceo-icon-background.svg';

export interface CEOPanelProps {
  title: string;
  tagline: string;
  profileImgURL: string;
}

const PROFILE_WIDTH_MD = 365;
const PROFILE_WIDTH_LG = 500;

const ProfilePicture = styled.img`
  ${width({ xs: '100%', md: PROFILE_WIDTH_MD, lg: PROFILE_WIDTH_LG, xl: 663 })}
  position: relative;
  z-index: 1;
`;

const ImageContainer = styled.div`
  line-height: 0;
  position: relative;
`;

const DottedCircleContainer = styled.div`
  ${width({ xs: 170, lg: 310 })}
  ${height({ xs: 170, lg: 310 })}
  ${left({
    xs: '100%',
    md: `${PROFILE_WIDTH_MD}px`,
    lg: `${PROFILE_WIDTH_LG}px`,
    xl: 'auto',
  })}
  ${right({ xl: '-100%' })}
  position: absolute;
  top: 0;
  transform: translate(-50%, -25%);

  ${match('md')`
    transform: translate(-45%, -25%);
  `};

  ${match('lg')`
    transform: translate(-25%, -25%);
  `};
`;

const SquiggleIconImageBackground = styled.div`
  ${height({ xs: 'calc(60vw * 1.64)', md: 447, lg: 612, xl: 812 })}
  ${left({ xs: '60%', md: '85%', lg: '80%' })}
  ${top({ xs: '-20%', md: '-30%' })}
  ${width({ xs: '60vw', md: 273, lg: 373, xl: 495 })}
  ${zIndex({ md: 1 })}
  background: url(${squiggleIcon}) no-repeat;
  background-position: center;
  background-size: cover;
  position: absolute;
`;

const TextWrapper = styled.div`
  ${backgroundColor('neutral.000')}
  ${marginTop({ xs: 'none', md: 'xl', xl: 'none' })}
  ${padding({ xs: 'none', md: 'xl' })}
  ${paddingRight({ xs: 'none', md: 'xxs', xl: 'none' })}
  ${position({ md: 'relative' })}
  ${zIndex({ md: 1 })}
`;

export const CEOPanel: React.FC<React.PropsWithChildren<CEOPanelProps>> = ({
  title,
  tagline,
  profileImgURL,
}) => {
  return (
    <Container>
      <Columns
        alignX={{ xs: 'center', md: 'right' }}
        alignY={{ xl: 'center' }}
        spaceY={{ xs: 'xl', md: 'none' }}
      >
        <Columns.Column width={{ xs: 1, md: 3 / 8, lg: 5 / 12 }}>
          <ImageContainer>
            <ProfilePicture src={profileImgURL} alt={title} />

            <DottedCircleContainer>
              <DottedCircle color="neutral.100" alpha={0.15} />
            </DottedCircleContainer>

            <SquiggleIconImageBackground />
          </ImageContainer>
        </Columns.Column>

        <Columns.Column width={{ xs: 1, md: 5 / 8, lg: 6 / 12 }}>
          <TextWrapper>
            <Box padding={{ xl: 'xxs' }} paddingRight={{ xl: 'lg' }}>
              <Stack spaceY={{ xs: 'sm', xl: 'md' }}>
                <Heading variant={2} component="h2">
                  {title}
                </Heading>
                <Text variant={1} color="neutral.080">
                  {tagline}
                </Text>
              </Stack>
            </Box>
          </TextWrapper>
        </Columns.Column>
      </Columns>
    </Container>
  );
};
